/**
 * CURLY ARROWS: where the electrons go, drawn over a structure.
 *
 * ── A SECOND AUTHORING PASS, WHICH IS WHAT THE OWNER SPOTTED ─────────────────────────────────
 * *"curly arrows in, but how would they work? it seems like another authoring level after the
 * reaction is written"* — exactly so. The molecule exists first; the arrows are an annotation
 * ON it, and they are stored with it rather than in the reaction's own text. Nothing about the
 * chemistry changes when one is added.
 *
 * ── WHAT AN ARROW POINTS AT ───────────────────────────────────────────────────────────────────
 * An atom, or a BOND. Both, routinely: a lone pair on an oxygen attacking a carbon is atom to
 * atom, a double bond attacking a proton is bond to atom, and heterolysis is bond to atom in the
 * other direction. This is the whole reason the renderer was chosen — OpenChemLib emits an
 * element per atom AND per bond, which SmilesDrawer does not, so a bond can be pointed at.
 *
 * ── AND WHY THE ANCHORS ARE INDICES INTO THE SMILES ───────────────────────────────────────────
 * An arrow drawn on a picture and stored as coordinates is wrong the moment the molecule is
 * redrawn at another size, in another theme, or by a later version of the library. Atom 3 of a
 * SMILES is atom 3 whatever the drawing does, so that is what is stored. The cost is that
 * EDITING the SMILES can leave an arrow pointing at an atom that is no longer there, which
 * `pruneArrows` is for: an arrow with nothing to point at is dropped rather than drawn into
 * empty space.
 */

export type AnchorKind = 'atom' | 'bond';

/** One end of an arrow: which kind of thing, and its index in the SMILES. */
export type Anchor = { kind: AnchorKind; index: number };

/**
 * Half an arrowhead means ONE electron, a full head means a pair.
 *
 * Not decoration. A fishhook is homolysis and a full arrow is heterolysis, and a student is
 * asked to tell them apart — drawing one for the other states the wrong mechanism.
 */
export type ArrowHead = 'pair' | 'single';

export type Arrow = { from: Anchor; to: Anchor; head: ArrowHead };

// ── Stored beside the molecule ───────────────────────────────────────────────

const KIND_CODE: Record<AnchorKind, string> = { atom: 'a', bond: 'b' };
const CODE_KIND: Record<string, AnchorKind> = { a: 'atom', b: 'bond' };
const HEAD_CODE: Record<ArrowHead, string> = { pair: '', single: '~' };

const anchorText = (a: Anchor) => `${KIND_CODE[a.kind]}${a.index}`;

/**
 * The arrows as one field, appended to the structure token: `[smiles:…|name|a3>b1,b1~>a0]`.
 *
 * A third field rather than a new token kind, so every reaction already saved still reads, and
 * so the arrows travel with the molecule they belong to through copy, paste and undo without
 * anything else having to know they exist.
 */
export function arrowsToText(arrows: Arrow[]): string {
  return arrows.map(a => `${anchorText(a.from)}${HEAD_CODE[a.head]}>${anchorText(a.to)}`).join(',');
}

const ONE = /^([ab])(\d+)(~?)>([ab])(\d+)$/;

/** Read them back. Anything malformed is dropped, never guessed at. */
export function arrowsFromText(text: string): Arrow[] {
  if (!text.trim()) return [];
  const out: Arrow[] = [];
  for (const part of text.split(',')) {
    const m = ONE.exec(part.trim());
    if (!m) continue;
    out.push({
      from: { kind: CODE_KIND[m[1]!]!, index: Number(m[2]) },
      to: { kind: CODE_KIND[m[4]!]!, index: Number(m[5]) },
      head: m[3] === '~' ? 'single' : 'pair',
    });
  }
  return out;
}

/**
 * Drop any arrow whose ends no longer exist.
 *
 * The SMILES is editable and the indices are into it, so shortening a chain can strand an arrow.
 * Dropping is the honest answer: an arrow pointing at an atom that is gone would be drawn
 * somewhere arbitrary, and a mechanism drawn wrong is worse than one not drawn.
 */
export function pruneArrows(arrows: Arrow[], atoms: number, bonds: number): Arrow[] {
  const ok = (a: Anchor) => a.index >= 0 && a.index < (a.kind === 'atom' ? atoms : bonds);
  return arrows.filter(a => ok(a.from) && ok(a.to));
}
